import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

// ONE picker, two entrances (C.8): the Pending Requests modal and the
// calendar's in-block click. Same chips, same conflict hints, same summary,
// same payload — there is no second implementation to drift.
//
// Span math lives HERE and nowhere else on the client, and it mirrors the
// server's validator exactly (scheduling_router.py:1795-1834):
//   start >= window start  AND  start + duration <= window end
export const SPAN_GRID_MINUTES = 15;

export const toMin = (hms) => {
  const p = String(hms || "0:0").split(":").map((x) => parseInt(x, 10) || 0);
  return p[0] * 60 + (p[1] || 0);
};
export const toHms = (min) =>
  `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}:00`;

export const fmt12 = (min) => {
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${String(m).padStart(2, "0")} ${h24 < 12 ? "AM" : "PM"}`;
};

// The chips: every grid-aligned start whose FULL appointment still ends
// inside the window. The last chip is therefore (window end − duration).
export function startChips(windowStart, windowEnd, durationMinutes, grid = SPAN_GRID_MINUTES) {
  const s = toMin(windowStart);
  const e = toMin(windowEnd || "") || s + durationMinutes;
  const first = Math.ceil(s / grid) * grid;
  const out = [];
  for (let t = first; t + durationMinutes <= e; t += grid) out.push(t);
  // A window shorter than one appointment still offers its start — the
  // server is the authority and will refuse it with a plain message.
  return out.length ? out : [s];
}

// Same arithmetic as the chips, applied to what is already on the books.
export function overlapsBooked(startMin, durationMinutes, booked) {
  const end = startMin + durationMinutes;
  return (booked || []).find((b) => {
    const bs = toMin(b.start_time);
    const be = toMin(b.end_time || "") || bs + SPAN_GRID_MINUTES;
    return startMin < be && bs < end;
  }) || null;
}

/**
 * props:
 *  window          { id, date, start_time, end_time }
 *  durationMinutes number (the clinic's setting; 15 = today)
 *  booked          [{ start_time, end_time, label }] on-the-books at this
 *                  location for the window's day. Advisory only.
 *  bookedLoading   bool — hints are lazy; absent hints never block.
 *  locationLabel   string
 *  value           selected start in minutes (null ⇒ window start)
 *  onChange        (minutes) => void
 */
export default function WindowTimePicker({
  window: win,
  durationMinutes,
  booked = [],
  bookedLoading = false,
  locationLabel = "",
  value = null,
  onChange,
}) {
  const chips = useMemo(
    () => startChips(win.start_time, win.end_time, durationMinutes),
    [win.start_time, win.end_time, durationMinutes],
  );
  // Default preselected = window start, so a one-click confirm behaves
  // exactly as it did before this ticket.
  const [selected, setSelected] = useState(value ?? chips[0]);
  useEffect(() => {
    const next = value ?? chips[0];
    setSelected(next);
    onChange?.(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win.id, durationMinutes]);

  const pick = (min) => { setSelected(min); onChange?.(min); };
  const end = selected + durationMinutes;

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
         data-testid={`window-time-picker-${win.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Start time
        </p>
        {bookedLoading && <Loader2 size={11} className="animate-spin text-slate-400" />}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((min) => {
          const on = min === selected;
          const clash = overlapsBooked(min, durationMinutes, booked);
          return (
            <button
              key={min}
              type="button"
              aria-pressed={on}
              onClick={() => pick(min)}
              title={clash
                ? `Overlaps ${fmt12(toMin(clash.start_time))} · ${clash.label || "booked"}`
                : undefined}
              data-testid={`start-chip-${win.id}-${toHms(min).slice(0, 5)}`}
              className={`min-h-[34px] px-2.5 rounded-md text-sm font-medium border transition-colors inline-flex items-center gap-1
                ${on ? "bg-emerald-600 border-emerald-600 text-white"
                     : clash ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                             : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}
            >
              {clash && <AlertTriangle size={11} />}
              {fmt12(min)}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-slate-600" data-testid={`picker-span-${win.id}`}>
        {fmt12(selected)} – {fmt12(end)}
        {locationLabel ? <span className="text-slate-400"> · {locationLabel}</span> : null}
      </p>
    </div>
  );
}
